"use client"

import { useEffect } from "react"

// Turning a vendor <script> snippet into a component that mounts and unmounts
// cleanly has a few non-obvious steps. The recipe, the traps and how to verify
// it in a browser are in apps/ashtrom-corporate/CLAUDE.md, "Third-party embed
// scripts". Read that before changing anything below.

const SRC =
  "https://ashtrom-bots-copilot-ui.orangecoast-581b5e51.swedencentral.azurecontainerapps.io/embed.js"

// embed.js builds the whole widget inside one shadow host appended to document.body
// and guards itself with a global so it only runs once per page load. Tearing the
// widget down therefore means removing the host *and* clearing that guard, or it
// never comes back when the visitor returns to the homepage.
const HOST_ID = "ashtrom-chat-host"
const LOADED_FLAG = "__ashtromChatLoaded"

const ATTRIBUTES: Record<string, string> = {
  "data-app-url":
    "https://ashtrom-bots-copilot-ui.orangecoast-581b5e51.swedencentral.azurecontainerapps.io/",
  "data-position": "right",
  "data-title": "העוזר החכם של אשטרום",
  "data-size": "56",
  "data-pulse": "on",
  "data-accent": "#1F4E8C",
  "data-accent-hover": "#2B63AE",
  "data-accent-active": "#163B6E",
  "data-intro":
    "שלום, אני העוזר החכם של אשטרום. אשמח לסייע באיתור מידע על הקבוצה, החברות, הפרויקטים והשירותים שלנו.",
}

// Module scope, not component state: StrictMode mounts the effect twice in dev and
// a visitor can navigate away mid-load, so both paths have to agree on whether the
// widget is currently wanted. A per-effect flag gets this wrong, because the stale
// effect's onload would tear down the widget the live one just created.
let mountCount = 0
let injected: HTMLScriptElement | null = null

function teardown() {
  document.getElementById(HOST_ID)?.remove()
  injected?.remove()
  injected = null
  delete (window as unknown as Record<string, unknown>)[LOADED_FLAG]
}

function inject() {
  // A script that was already in flight may have built a widget between the last
  // teardown and now. Clear it before clearing the guard, or embed.js sees no guard
  // and builds a second host alongside the first, giving two launchers.
  teardown()

  const script = document.createElement("script")
  script.src = SRC
  Object.entries(ATTRIBUTES).forEach(([name, value]) => script.setAttribute(name, value))

  // Removing a script that has already started does not cancel it, so a visitor who
  // leaves mid-load would otherwise strand the widget on the next page.
  script.onload = () => {
    if (mountCount === 0) teardown()
  }

  injected = script
  document.body.appendChild(script)
}

// The <Script strategy="lazyOnload"> this replaced waited for the browser to go idle
// after load. An effect runs at hydration, which would put the vendor request in
// competition with the hero media on the heaviest page of the site, so hold it back
// to roughly where it was before.
function whenIdle(run: () => void) {
  const schedule = () => {
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(run)
    else window.setTimeout(run, 1)
  }

  if (document.readyState === "complete") schedule()
  else window.addEventListener("load", schedule, { once: true })
}

export default function ChatbotEmbed() {
  useEffect(() => {
    mountCount += 1
    if (mountCount === 1) {
      whenIdle(() => {
        // The visitor may have left, or a second mount may already have injected,
        // between scheduling and running.
        if (mountCount > 0 && !injected) inject()
      })
    }

    return () => {
      mountCount -= 1
      if (mountCount === 0) teardown()
    }
  }, [])

  return null
}
